// ** Designed for switching back to React app only. **

/**
 * Utility for saving currentUser in localStorage with key: HCS{basename}-{storeName}-currentUser
 *
 */

import { CHECKOUT_CURRENT_ADDRESS_KEY } from '@/components/content/CheckOutV2/constants';
import { REMEMBERED_LOGON_ID } from '@/data/constants/cookie';
import { PREVIEW_TOKEN_PARAM } from '@/data/constants/preview';
import {
	CURRENT_USER_KEY,
	KEY_PREFIX,
	PERMANENT_STORE_DAYS,
	PERSONALIZATION_ID,
	STORE_NAME,
} from '@/data/forReactAndMobile/constants';
import { ComIbmCommerceRestMemberHandlerLoginIdentityHandlerUserIdentity } from 'integration/generated/transactions/data-contracts';
import { attempt, isError } from 'lodash';
import { setCookie } from 'shared/custom/utils/cookieHandler';

/**
 * This structure is same as what React store used,except adding generatedCookie flag to track if NextJS store cookies generated or not.
 */
export type CurrentUser = ComIbmCommerceRestMemberHandlerLoginIdentityHandlerUserIdentity & {
	privacyNoticeVersion?: string;
	marketingTrackingConsent?: string;
	userLoggedIn: boolean;
	isGuest: boolean;
	generatedCookie: boolean;
	newGuestToken?: boolean;
	rememberMe?: boolean;
};

export type PreviewUser = {
	WCPreviewToken: string;
};
const setWithExpiry = (key: string, value: any, days: number) => {
	const now = new Date();
	const expiry = now.getTime() + days * 24 * 60 * 60 * 1000;
	const item = {
		value,
		expiry,
	};
	localStorage.setItem(key, JSON.stringify(item));
};

/**
 * Save current user object in local storage
 * @param baseName HDM-B2C-Store in local, empty in non-local.
 * @param user
 */
export const saveCurrentUserToStorage = (baseName: string, user: CurrentUser) => {
	if (typeof window !== 'undefined') {
		// Save user object
		const key = `${KEY_PREFIX}${baseName}-${STORE_NAME}-${CURRENT_USER_KEY}`;
		const newCurrentUser = {
			...user,
			lastUpdated: Date.now(),
			userLoggedIn: user?.userLoggedIn ?? false,
			isGuest: user?.isGuest ?? true,
			newGuestToken: user?.newGuestToken ?? false,
		};
		localStorage.setItem(key, JSON.stringify(newCurrentUser));
		// Save personalizationID with expiry
		if (user.personalizationID) {
			const personalizationIDKey = `${KEY_PREFIX}${baseName}-${STORE_NAME}-${PERSONALIZATION_ID}`;
			setWithExpiry(personalizationIDKey, user.personalizationID, PERMANENT_STORE_DAYS);
		}
	}
};
/**
 * Clear CurrentUser object
 * @param baseName
 */
export const clearCurrentUserFromStorage = (baseName: string) => {
	if (typeof window !== 'undefined') {
		// Remove user object to remove current user session
		const key = `${KEY_PREFIX}${baseName}-${STORE_NAME}-${CURRENT_USER_KEY}`;
		localStorage.removeItem(key);
		sessionStorage.removeItem(key);
		sessionStorage.removeItem(CHECKOUT_CURRENT_ADDRESS_KEY);
		setCookie(REMEMBERED_LOGON_ID, '', 0);
	}
};

/**
 * Fetch current user object, parse as JSON object.
 * @param baseName HDM-B2C-Store in local, empty in non-local.
 * @returns
 */
export const getCurrentUserFromStorage = (baseName: string): CurrentUser | null => {
	let result;
	if (typeof window !== 'undefined') {
		const currentUserKey = `${KEY_PREFIX}${baseName}-${STORE_NAME}-${CURRENT_USER_KEY}`;
		let item = localStorage.getItem(currentUserKey);
		if (!item) {
			item = sessionStorage.getItem(currentUserKey);
			if (item) {
				localStorage.setItem(currentUserKey, item);
				sessionStorage.removeItem(currentUserKey);
			}
		}
		result = item === null ? null : attempt(JSON.parse, item);
		if (isError(result)) {
			result = item;
		}
	}
	return result;
};
/**
 *
 * @param baseName
 * @param token
 * Next code is to save and get the preview token values which are created using
 * the CMC preview mode and helps the business team for testing.
 * The purpose is to save it as react in the session storage and use them as a header
 * in the espots call
 */
export const savePreviewToken = (baseName: string, token: string) => {
	if (typeof window !== 'undefined') {
		const previewUserKey = `${KEY_PREFIX}${baseName}-${STORE_NAME}-${PREVIEW_TOKEN_PARAM}`;
		const newPreviewToken = {
			WCPreviewToken: token,
		};
		sessionStorage.setItem(previewUserKey, JSON.stringify(newPreviewToken));
	}
};
export const getPreviewToken = (baseName: string): PreviewUser | null => {
	let result;
	if (typeof window !== 'undefined') {
		const previewUserKey = `${KEY_PREFIX}${baseName}-${STORE_NAME}-${PREVIEW_TOKEN_PARAM}`;
		const previewToken = sessionStorage.getItem(previewUserKey);
		result = previewToken === null ? null : attempt(JSON.parse, previewToken);
		if (isError(previewToken)) {
			result = previewToken;
		}
	}
	return result;
};
